import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Chip,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Person,
  AccessTimeSharp,
  TaskAlt as TaskAltIcon
} from '@mui/icons-material';

const AbsenceCalendar = ({ absences = [], onDateClick, currentDate = new Date() }) => {
  const theme = useTheme();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [selectedDate, setSelectedDate] = useState(today);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(currentDate.getFullYear());

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get days in month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday, 6 = Saturday)
  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  // Format date for comparison
  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Check if date is Sunday
  const isSunday = (date) => {
    return date.getDay() === 0;
  };

  // Check if date has absences
  const getAbsencesForDate = (date) => {
    // If it's Sunday, return empty array (no absences)
    if (isSunday(date)) {
      return [];
    }

    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0); // normalize

    return absences.filter(absence => {
      const startDate = new Date(absence.date);
      startDate.setHours(0, 0, 0, 0);

      // Check if absence has an endDate, otherwise it's a single day absence
      const endDate = absence.endDate ? new Date(absence.endDate) : null;
      if (endDate) endDate.setHours(0, 0, 0, 0);

      // For single day absences, just check if dates match
      if (!endDate) {
        return formatDate(targetDate) === formatDate(startDate);
      }

      // For multi-day absences, check if target date is in range
      return targetDate >= startDate && targetDate <= endDate;
    });
  };

  const getAbsenceStyle = (date, dayAbsences) => {
    // If it's Sunday, apply light green background
    if (isSunday(date)) {
      return {
        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.success.main, 0.1) : '#e8f5e9',
        color: 'text.secondary',
        cursor: 'default'
      };
    }

    const count = dayAbsences.length;
    if (count === 0) return {};

    // Compute red shade: the more absences, the darker
    const intensity = Math.min(count / 5, 1); // Clamp between 0 and 1

    // Use theme error color with varying opacity/shade
    const bgcolor = alpha(theme.palette.error.main, 0.3 + (intensity * 0.7));

    return {
      bgcolor: bgcolor,
      color: 'white',
      borderRadius: 1,
      transition: 'background-color 0.3s ease'
    };
  };

  // Navigation functions
  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const goToToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    setSelectedDate(today);
  };

  // Handle date click
  const handleDateClick = (day) => {
    const clickedDate = new Date(currentYear, currentMonth, day);

    // Don't process clicks on Sundays
    if (isSunday(clickedDate)) {
      return;
    }

    setSelectedDate(clickedDate);
    const dayAbsences = getAbsencesForDate(clickedDate);
    if (dayAbsences.length > 0 && onDateClick) {
      onDateClick(clickedDate, dayAbsences);
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDayOfMonth = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<Box key={`empty-${i}`} sx={{ p: 1 }} />);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dayAbsences = getAbsencesForDate(date);
      const isToday = formatDate(date) === formatDate(new Date(new Date().setHours(0, 0, 0, 0)));
      const isSelected = formatDate(date) === formatDate(selectedDate);
      const isSundayDay = isSunday(date);
      const absenceStyle = getAbsenceStyle(date, dayAbsences);

      days.push(
        <Tooltip
          key={day}
          title={
            isSundayDay
              ? 'Sunday - Holiday'
              : dayAbsences.length > 0
                ? `${dayAbsences.length} absence(s) - Click for details`
                : ''
          }
          arrow
        >
          <Box
            onClick={() => handleDateClick(day)}
            sx={{
              p: 1,
              textAlign: 'center',
              cursor: isSundayDay ? 'default' : (dayAbsences.length > 0 ? 'pointer' : 'default'),
              borderRadius: 1,
              minHeight: 32,
              height: '8vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              border: isToday ? `2px solid ${theme.palette.primary.main}` : 'none',
              boxShadow: isSelected ? `0 0 0 2px ${theme.palette.primary.main}` : 'none',
              transition: 'all 0.2s ease',
              bgcolor: 'background.paper',
              '&:hover': {
                bgcolor: isSundayDay ? undefined : (dayAbsences.length > 0 ? undefined : 'action.hover'),
                transform: dayAbsences.length > 0 ? 'scale(1.05)' : 'none'
              },
              ...absenceStyle
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: isToday ? 'bold' : 'normal',
                color: isSundayDay
                  ? 'error.main'
                  : (absenceStyle.color || (isToday ? 'primary.main' : 'text.primary'))
              }}
            >
              {day}
            </Typography>
            {!isSundayDay && dayAbsences.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  fontSize: '0.65rem',
                  fontWeight: 'bold',
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  borderRadius: '50%',
                  width: 14,
                  height: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {dayAbsences.length}
              </Box>
            )}
          </Box>
        </Tooltip>
      );
    }

    return days;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
        width: '100%',
        height: '100%',
      }}
    >
      {/* Calendar Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
          width: '100%',
        }}
      >
        <IconButton onClick={() => navigateMonth('prev')} size="small">
          <ChevronLeft />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            color: 'primary.main',
            textAlign: 'center',
            flex: 1
          }}
        >
          {months[currentMonth]} {currentYear}
        </Typography>

        <IconButton onClick={() => navigateMonth('next')} size="small">
          <ChevronRight />
        </IconButton>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
        {daysOfWeek.map(day => (
          <Typography
            key={day}
            variant="caption"
            align="center"
            sx={{
              fontWeight: 'bold',
              color: day === 'Sun' ? 'error.main' : 'text.secondary',
              p: 1,
            }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      {/* Calendar Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, width: '100%', height: '100%' }}>
        {generateCalendarDays()}
      </Box>

      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1.5,
          mt: 2,
          pt: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          flexWrap: 'wrap'
        }}
      >
        <Chip
          icon={<Person sx={{ color: 'white !important' }} />}
          label="Full Day"
          size="small"
          sx={{ bgcolor: 'error.main', color: 'white' }}
        />
        <Chip
          icon={<AccessTimeSharp sx={{ color: 'white !important' }} />}
          label="Partial Day"
          size="small"
          sx={{ bgcolor: 'warning.main', color: 'white' }}
        />
        <Chip
          icon={<TaskAltIcon />}
          label="Today"
          size="small"
          variant="outlined"
          onClick={goToToday}
          sx={{ borderColor: 'primary.main', color: 'primary.main', cursor: 'pointer' }}
        />
        <Chip
          label="Sunday/Holiday"
          size="small"
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.success.main, 0.2) : '#e8f5e9',
            color: 'success.dark',
          }}
        />
      </Box>
    </Paper>
  );
};

export default AbsenceCalendar;